import { useQuery } from '@apollo/client';
import { useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type { LitterGetLitterQuery, LitterGetLitterQueryVariables } from '@gql/graphql';
import { useMemo } from 'react';

export const LITTER_GET_LITTER = graphql(`
  query LitterGetLitter(
    $litterWhere: LitterWhereInput!
    $breedCardInfoTranslationsWhere: DogBreedTranslationWhereInput
    $breederCardInfoTranslationsWhere: DogBreederTranslationWhereInput
    $breederCardInfoTranslationsWhereBreed: DogBreedTranslationWhereInput
    $breederCardInfoBreedImgsTake: Int
    $breederCardInfoLittersWhere: LitterWhereInput
  ) {
    findManyLitter(where: $litterWhere, take: 1) {
      id
      availabilityDate
      birthday
      microchipped
      vaccinationDate
      vaccinated
      dewormed
      pedigree
      pedigreeNumbers
      certified
      price
      priceFrom
      priceTo
      fixedPrice
      description
      malesCount
      femalesCount
      malesLeftCount
      femalesLeftCount
      microchips
      born

      translations {
        id
        url
        seoKeywords
        seoDescription
        seoTitle
        language
      }

      imgs {
        id
        small
        medium
        large
      }

      dogBreed {
        ...BreedCardInfo
        name
        baseBreedName
      }

      breeder {
        ...BreederCardInfo
        level
        profileImg
        agreementNumber
        lastConnectedType
        whatsappNumber
        account {
          language
          address {
            zip
            street
            city
            region
            country
          }
        }
      }

      parents {
        id
        parentType
        parent {
          id
          name
          imgs {
            id
            small
            large
          }
          birthday
          dogInfos {
            id
            microshipNumber
            pedigreeNumber
            pedigree
            competitionTitles
            healthTestsResults
          }
        }
      }
    }
  }
`);

export const useGetLitter = (id: string) => {
  const { language } = useRouter();

  const variables: LitterGetLitterQueryVariables = useMemo(() => {
    return {
      litterWhere: {
        AND: [
          { OR: [{ archived: { equals: false } }, { archived: { equals: null } }] },
          { breeder: { is: { certified: { equals: true }, public: { equals: true } } } },
          { translations: { some: { url: { equals: id } } } },
        ],
      },
      breedCardInfoTranslationsWhere: { language: { equals: language } },
      breederCardInfoTranslationsWhere: { language: { equals: language } },
      breederCardInfoTranslationsWhereBreed: { language: { equals: language } },
      breederCardInfoBreedImgsTake: 1,
      breederCardInfoLittersWhere: {
        archived: { equals: false },
        adminValidated: { equals: true },
      },
    } as LitterGetLitterQueryVariables;
  }, [id, language]);

  const { data, error, loading } = useQuery<LitterGetLitterQuery, LitterGetLitterQueryVariables>(
    LITTER_GET_LITTER,
    {
      variables,
    },
  );

  return {
    litter: data?.findManyLitter?.[0],
    litterError: error,
    litterLoading: loading,
    litterVariables: variables,
  };
};
